import { Fragment, useEffect, useState, type ReactNode } from 'react';
import { REVIEWED_LANGS, formatDate, getLang, locale, t, type Key, type Lang } from '../i18n';
import { loadLegal, peekLegal, type LegalBlock, type LegalDocId, type LegalSection, type LegalTexts } from '../i18n/legal';
import { Icon } from './icons';
import { LEGAL_INFO, LEGAL_UPDATED, isTodo, todoFields, type LegalField } from './legalInfo';
import { PageHead, type LegalPageId, type Page } from './MenuShell';

export const LEGAL_PAGES: readonly LegalPageId[] = ['legal', 'privacy', 'terms'];
const DOC_OF: Record<LegalPageId, LegalDocId> = { legal: 'notice', privacy: 'privacy', terms: 'terms' };
const PAGE_OF: Record<LegalDocId, LegalPageId> = { notice: 'legal', privacy: 'privacy', terms: 'terms' };

const isField = (k: string): k is LegalField => k in LEGAL_INFO;

function countryName(code: string): string {
  try {
    return new Intl.DisplayNames([locale()], { type: 'region' }).of(code.toUpperCase()) ?? code;
  } catch {
    return code;
  }
}

/** Valeur d'un champ de `legalInfo.ts` : « TODO » en évidence tant qu'il n'est pas rempli, e-mail cliquable. */
export function LegalValue({ field }: { field: LegalField }) {
  const v = LEGAL_INFO[field];
  if (isTodo(v))
    return (
      <mark className="legal-todo" data-field={field}>
        TODO
      </mark>
    );
  if (field === 'email') return <a href={`mailto:${v}`}>{v}</a>;
  if (field === 'serverCountry') return <>{countryName(v)}</>;
  return <>{v}</>;
}

/** Lien vers une page légale : navigation interne dans le menu, nouvel onglet en partie (sans `go`). */
export function LegalLink({ page, go, children }: { page: LegalPageId; go?: (p: Page) => void; children?: ReactNode }) {
  return (
    <a
      className="legal-link"
      href={`#${page}`}
      target={go ? undefined : '_blank'}
      rel={go ? undefined : 'noopener'}
      onClick={(e) => {
        if (!go || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
        e.preventDefault();
        go(page);
      }}
    >
      {children ?? t(`legal_${page}` as Key)}
    </a>
  );
}

/** Les trois documents, en ligne (profil, aide, inscription). */
export function LegalLinks({ go, className = '' }: { go?: (p: Page) => void; className?: string }) {
  return (
    <nav className={`legal-links ${className}`} aria-label={t('legalNav')}>
      {LEGAL_PAGES.map((p) => (
        <LegalLink key={p} page={p} go={go} />
      ))}
    </nav>
  );
}

// Adresse : caractères ASCII d'URL seulement, sans ponctuation finale (la ponctuation chinoise ou japonaise collée
// à l'adresse reste hors du lien).
const SLOT_RE = /(\{\w+\}|https:\/\/[\w.~%/?#=&+-]*[\w/~%#=&+-])/g;

/**
 * Texte avec champs : {owner}, {email}… viennent de `legalInfo.ts`, les autres accolades de `slots`
 * (liens vers les autres pages) ; les adresses https deviennent des liens.
 */
export function legalText(text: string, slots: Partial<Record<string, ReactNode>> = {}): ReactNode[] {
  return text.split(SLOT_RE).map((part, i) => {
    const m = /^\{(\w+)\}$/.exec(part);
    if (m && m[1] in slots) return <Fragment key={i}>{slots[m[1]]}</Fragment>;
    if (m && isField(m[1])) return <LegalValue key={i} field={m[1]} />;
    if (part.startsWith('https://'))
      return (
        <a key={i} href={part} target="_blank" rel="noopener noreferrer">
          {part.slice('https://'.length)}
        </a>
      );
    return part;
  });
}

/** Un champ laissé vide ('') dans `legalInfo.ts` masque la ligne qui l'utilise. */
function hasEmptyField(text: string): boolean {
  return [...text.matchAll(/\{(\w+)\}/g)].some((m) => isField(m[1]) && LEGAL_INFO[m[1]].trim() === '');
}

function visible(b: LegalBlock): LegalBlock | null {
  if (typeof b === 'string') return hasEmptyField(b) ? null : b;
  if ('list' in b) {
    const list = b.list.filter((x) => !hasEmptyField(x));
    return list.length ? { list } : null;
  }
  return b;
}

function Block({ b, slots }: { b: LegalBlock; slots: Record<string, ReactNode> }) {
  if (typeof b === 'string') return <p>{legalText(b, slots)}</p>;
  if ('list' in b)
    return (
      <ul className="legal-list">
        {b.list.map((x, i) => (
          <li key={i}>{legalText(x, slots)}</li>
        ))}
      </ul>
    );
  const [head, ...rows] = b.table;
  return (
    <table className="legal-table">
      <thead>
        <tr>
          {head.map((h, i) => (
            <th key={i} scope="col">
              {h}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {rows.map((r, i) => (
          <tr key={i}>
            {r.map((c, j) =>
              j === 0 ? (
                <th key={j} scope="row" data-label={head[j]}>
                  {legalText(c, slots)}
                </th>
              ) : (
                <td key={j} data-label={head[j]}>
                  {legalText(c, slots)}
                </td>
              ),
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Mentions légales, confidentialité, conditions de vente : sommaire, sections numérotées, champs à compléter. */
export function LegalPage({ page, go }: { page: LegalPageId; go: (p: Page) => void }) {
  const lang = getLang();
  const [french, setFrench] = useState(false);
  const shown: Lang = french ? 'fr' : lang;
  const [texts, setTexts] = useState<{ lang: Lang; legal: LegalTexts } | null>(() => {
    const legal = peekLegal(shown);
    return legal ? { lang: shown, legal } : null;
  });
  useEffect(() => {
    let live = true;
    loadLegal(shown)
      .then((legal) => {
        if (live) setTexts({ lang: shown, legal });
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [shown]);

  const ready = texts?.lang === shown ? texts.legal : peekLegal(shown);
  const doc = ready?.[DOC_OF[page]];
  // Liens entre documents, intitulés par le titre complet du document lié.
  const slots: Record<string, ReactNode> = {};
  if (ready)
    for (const id of Object.keys(PAGE_OF) as LegalDocId[])
      slots[id] = (
        <LegalLink page={PAGE_OF[id]} go={go}>
          {ready[id].title}
        </LegalLink>
      );
  const sections = (doc?.sections ?? [])
    .map((s): LegalSection => ({ ...s, body: s.body.map(visible).filter((b): b is LegalBlock => !!b) }))
    .filter((s) => s.body.length > 0);
  const draft = todoFields().length > 0;

  return (
    <div className="page legal-page">
      <PageHead title={doc?.title ?? t(`legal_${page}` as Key)} sub={doc?.lead}>
        <span className="badge legal-date">{t('legalUpdated', { d: formatDate(Date.parse(`${LEGAL_UPDATED}T12:00:00Z`)) })}</span>
      </PageHead>

      <nav className="legal-tabs" aria-label={t('legalNav')}>
        {LEGAL_PAGES.map((p) => (
          <a
            key={p}
            href={`#${p}`}
            className={p === page ? 'on' : ''}
            aria-current={p === page ? 'page' : undefined}
            onClick={(e) => {
              if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
              e.preventDefault();
              go(p);
            }}
          >
            {t(`legal_${p}` as Key)}
          </a>
        ))}
      </nav>

      {draft && (
        <p className="legal-draft" role="note">
          <Icon name="alert" size={18} />
          <span>{t('legalDraft')}</span>
        </p>
      )}
      {lang !== 'fr' && (
        <div className="legal-lang" role="note">
          <span>
            {t('legalTranslated')}
            {!REVIEWED_LANGS.includes(lang) && ` ${t('translationReview')}`}
          </span>
          <button type="button" className="btn btn-ghost btn-small" onClick={() => setFrench((f) => !f)}>
            {french ? t('legalShowMine') : t('legalShowFr')}
          </button>
        </div>
      )}

      {!doc ? (
        <p className="wip">{t('loading')}</p>
      ) : (
        <div className="legal-layout">
          <nav className="plate legal-toc" aria-label={t('legalToc')}>
            <h2 className="plate-title">{t('legalToc')}</h2>
            <ol>
              {sections.map((s, i) => (
                <li key={s.id}>
                  <button type="button" onClick={() => document.getElementById(`legal-${s.id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' })}>
                    <span className="num">{i + 1}</span>
                    {s.title}
                  </button>
                </li>
              ))}
            </ol>
          </nav>
          <article className="legal-doc" lang={shown} dir={french ? 'ltr' : undefined}>
            {sections.map((s, i) => (
              <section key={s.id} className="plate legal-section" id={`legal-${s.id}`} aria-labelledby={`legal-h-${s.id}`}>
                <h2 className="legal-h" id={`legal-h-${s.id}`}>
                  <span className="legal-num num" aria-hidden>
                    {i + 1}
                  </span>
                  {s.title}
                </h2>
                {s.body.map((b, j) => (
                  <Block key={j} b={b} slots={slots} />
                ))}
              </section>
            ))}
          </article>
        </div>
      )}
    </div>
  );
}
